import { useState, useEffect } from "react";
import {
  Form,
  Input,
  Button,
  Upload,
  Modal,
  message,
  Space,
  Select,
  Spin,
  DatePicker,
  Switch,
  InputNumber,
} from "antd";
import { PlusOutlined, MinusCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";
import { useHttp } from "../../../utils/DataHendler";

const getBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

export const JobEditForm = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { code } = useParams(); // Mendapatkan ID job dari URL
  const { uploadFile, postData, getData, patchData } = useHttp();
  
  const [fileList, setFileList] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [loadingData, setLoadingData] = useState(false);

  // State untuk dropdown
  const [departments, setDepartments] = useState([]);
  const [jobCategories, setJobCategories] = useState([]);
  const [loadingDepartment, setLoadingDepartment] = useState(false);
  const [loadingCategory, setLoadingCategory] = useState(false);

  // Fetch department
  const getDepartment = async () => {
    setLoadingDepartment(true);
    try {
      const response = await getData("/department", {}, true);
      setDepartments(response?.data?.data || []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingDepartment(false);
    }
  };

  // Fetch job category
  const getJobCategory = async () => {
    setLoadingCategory(true);
    try {
      const response = await getData("/job-category", {}, true);
      setJobCategories(response?.data?.data || []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingCategory(false);
    }
  };

  // Fetch job data untuk edit
  const getJobData = async () => {
    if (!code) return;
    
    setLoadingData(true);
    try {
      const response = await getData(`/job/${code}`, {}, true);
      const jobData = response?.data;
      
      if (jobData) {
        // Set form values
        form.setFieldsValue({
          title: jobData.title,
          email: jobData.email,
          description: jobData.description,
          department_id: jobData.department_id,
          job_category_id: jobData.job_category_id,
          requirement: jobData.requirement || [],
          deadline: jobData.deadline ? dayjs(jobData.deadline) : dayjs().add(30, 'days'),
          is_active: jobData.is_active !== undefined ? jobData.is_active : true,
          required_position: jobData.required_position || 1
        });
        
        // Set existing image if available
        if (jobData.image_url) {
          setFileList([{
            uid: '-1',
            name: 'current-image',
            status: 'done',
            url: jobData.image_url,
          }]);
        }
      }
    } catch (error) {
      console.error('Error fetching job data:', error);
      message.error('Gagal memuat data job');
      navigate('/user'); // Redirect jika gagal load data
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    getDepartment();
    getJobCategory();
    getJobData(); // Fetch job data saat component mount
  }, [code]);

  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }
    setPreviewImage(file.url || file.preview);
    setPreviewVisible(true);
    setPreviewTitle(
      file.name || file.url?.substring(file.url.lastIndexOf("/") + 1)
    );
  };

  const handleChange = ({ fileList: newFileList }) => {
    setFileList(newFileList);
    if (newFileList.length > 0) {
      setSelectedFile(newFileList[0].originFileObj);
    } else {
      setSelectedFile(null);
    }
  };

  const beforeUpload = (file) => {
    // const isJpgOrPng =
    //   file.type === "image/jpeg" ||
    //   file.type === "image/jpg" ||
    //   file.type === "image/svg" ||
    //   file.type === "image/png";
    // if (!isJpgOrPng) {
    //   message.error("Hanya file JPG/PNG yang diperbolehkan!");
    //   return Upload.LIST_IGNORE;
    // }

    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error("Ukuran gambar tidak boleh lebih dari 5MB!");
      return Upload.LIST_IGNORE;
    }

    return true;
  };

  const handleCancel = () => setPreviewVisible(false);

  const onFinish = async (values) => {
    setLoadingSubmit(true);

    

    // console.log(values)
    try {
      let imageUrl = fileList[0]?.url; // Use existing image URL if no new file
      
      // Upload new image if selected
      if (selectedFile) {
        const result = await uploadFile("/upload/file", selectedFile);
        imageUrl = result.data.preview_url || result?.data?.url;
        
        if (!imageUrl) {
          throw new Error("Upload tidak mengembalikan URL gambar.");
        }
      }
      
      if (!imageUrl) {
        message.error("Gambar diperlukan.");
        setLoadingSubmit(false);
        return;
      }

      // Update job menggunakan PUT method
      await patchData(
        `/job`,
        {
          id : code,
          title: values.title,
          description: values.description,
          department_id: parseInt(values.department_id),
          job_category_id: parseInt(values.job_category_id),
          image_url: imageUrl,
          email: values.email,
          requirement: values.requirement || [],
          deadline: values.deadline ? dayjs(values.deadline).format('YYYY-MM-DD') : null,
          is_active: values.is_active !== undefined ? values.is_active : true,
          required_position: parseInt(values.required_position) || 1,
        },
        {},
        true
      );

      message.success("Lowongan berhasil diperbarui.");
      navigate("/user");
    } catch (err) {
      console.error("Update failed", err);
      message.error("Gagal memperbarui data job.");
    } finally {
      setLoadingSubmit(false);
    }
  };

  if (loadingData) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px',
        fontSize: '16px',
        color: '#666'
      }}>
        <Space>
          <Spin size="large" />
          <span>Memuat data job...</span>
        </Space>
      </div>
    );
  }

  return (
    <div style={{ 
      maxWidth: 800, 
      margin: "0 auto", 
      padding: "24px",
      backgroundColor: "#fff",
      borderRadius: "8px",
      boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
    }}>
      <div style={{ 
        marginBottom: "24px", 
        textAlign: "center",
        borderBottom: "1px solid #f0f0f0",
        paddingBottom: "16px"
      }}>
        <h2 style={{ 
          margin: 0, 
          color: "#1890ff",
          fontSize: "24px",
          fontWeight: "600"
        }}>
          Edit Lowongan Kerja
        </h2>
      </div>
      
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        style={{ width: "100%" }}
      >
      <Form.Item
        name="title"
        label="Judul Pekerjaan"
        rules={[{ required: true, message: "Judul pekerjaan wajib diisi" }]}
      >
        <Input placeholder="Masukkan judul pekerjaan" />
      </Form.Item>

      <Form.Item
        name="email"
        label="Email"
        rules={[
          { type: "email", message: "Format email tidak valid" }
        ]}
      >
        <Input placeholder="Masukkan Email (Opsional)" />
      </Form.Item>

      <Form.Item
        name="description"
        label="Deskripsi"
        rules={[{ required: true, message: "Deskripsi wajib diisi" }]}
      >
        <Input.TextArea rows={4} placeholder="Masukkan deskripsi pekerjaan" />
      </Form.Item>

      <Form.Item
        name="department_id"
        label="Department"
        rules={[{ required: true, message: "Pilih department" }]}
      >
        <Select
          loading={loadingDepartment}
          disabled={loadingDepartment}
          placeholder="Pilih Department"
          options={departments.map((el) => ({
            value: el.id,
            label: el.name,
          }))}
        />
      </Form.Item>

      <Form.Item
        name="job_category_id"
        label="Job Category"
        rules={[{ required: true, message: "Pilih job category" }]}
      >
        <Select
          loading={loadingCategory}
          disabled={loadingCategory}
          placeholder="Pilih Job Category"
          options={jobCategories.map((el) => ({
            value: el.id,
            label: el.name,
          }))}
        />
      </Form.Item>

      <Form.Item
        name="deadline"
        label="Deadline Lamaran"
        initialValue={dayjs().add(30, 'days')}
      >
        <DatePicker 
          style={{ width: '100%' }}
          placeholder="Pilih tanggal deadline"
          format="DD/MM/YYYY"
          disabledDate={(current) => current && current < dayjs().startOf('day')}
        />
      </Form.Item>

      <Form.Item
        name="required_position"
        label="Jumlah Posisi yang Dibutuhkan"
        rules={[{ required: true, message: "Jumlah posisi wajib diisi" }]}
        initialValue={1}
      >
        <InputNumber 
          min={1}
          max={1000}
          style={{ width: '100%' }}
          placeholder="Masukkan jumlah posisi"
        />
      </Form.Item>

      <Form.Item
        name="is_active"
        label="Status Aktif"
        valuePropName="checked"
        initialValue={true}
      >
        <Switch 
          checkedChildren="Aktif" 
          unCheckedChildren="Tidak Aktif"
          defaultChecked={true}
        />
      </Form.Item>

      <Form.List
        name="requirement"
        rules={[
          {
            validator: async (_, requirements) => {
              if (!requirements || requirements.length < 1) {
                return Promise.reject(
                  new Error("Minimal 1 persyaratan diperlukan")
                );
              }
            },
          },
        ]}
      >
        {(fields, { add, remove }) => (
          <>
            <Form.Item label="Persyaratan" required style={{ marginBottom: 0 }}>
              {fields.map((field) => (
                <Space
                  key={field.key}
                  style={{ display: "flex", marginBottom: 8 }}
                  align="baseline"
                >
                  <Form.Item
                    {...field}
                    style={{ flex: 1 }}
                    rules={[
                      {
                        required: true,
                        message: "Persyaratan tidak boleh kosong",
                      },
                    ]}
                  >
                    <Input placeholder="Masukkan persyaratan" />
                  </Form.Item>
                  <MinusCircleOutlined onClick={() => remove(field.name)} />
                </Space>
              ))}
              <Button
                type="dashed"
                onClick={() => add()}
                block
                icon={<PlusOutlined />}
              >
                Tambah Persyaratan
              </Button>
            </Form.Item>
          </>
        )}
      </Form.List>

      <br />
      <Form.Item label="Upload Flayer" required>
        <Upload
          listType="picture-card"
          fileList={fileList}
          onPreview={handlePreview}
          onChange={handleChange}
          beforeUpload={beforeUpload}
          maxCount={1}
        >
          {fileList.length >= 1 ? null : (
            <div>
              <PlusOutlined />
              <div style={{ marginTop: 8 }}>Upload</div>
            </div>
          )}
        </Upload>

        <Modal
          open={previewVisible}
          title={previewTitle}
          footer={null}
          onCancel={handleCancel}
        >
          <img alt="preview" style={{ width: "100%" }} src={previewImage} />
        </Modal>
      </Form.Item>

        <Form.Item style={{ marginTop: "32px", marginBottom: 0 }}>
          <div style={{ 
            display: "flex", 
            gap: "12px", 
            justifyContent: "center",
            paddingTop: "16px",
            borderTop: "1px solid #f0f0f0"
          }}>
            <Button 
              loading={loadingSubmit} 
              type="primary" 
              htmlType="submit"
              size="large"
              style={{ 
                minWidth: "120px",
                height: "40px",
                borderRadius: "6px",
                fontWeight: "500"
              }}
            >
              Update
            </Button>
            <Button
              size="large"
              style={{ 
                minWidth: "120px",
                height: "40px",
                borderRadius: "6px"
              }}
              onClick={() => {
                form.resetFields();
                setFileList([]);
                setSelectedFile(null);
                getJobData(); // Reset ke data original
              }}
            >
              Reset
            </Button>
            <Button
              size="large"
              style={{ 
                minWidth: "120px",
                height: "40px",
                borderRadius: "6px"
              }}
              onClick={() => navigate("/user")}
            >
              Batal
            </Button>
          </div>
        </Form.Item>
      </Form>
    </div>
  );
};
